import { SerializeQueryArgs, defaultSerializeQueryArgs } from '@reduxjs/toolkit/query';

/**
 * Cache-key serializer that scopes every RTK Query cache entry to the tenant
 * the request will actually be sent for.
 *
 * RTK Query keys a cache entry by `endpointName(serializedArg)`. The tenant is
 * not part of any argument — it travels as the `X-Tenant` header — and most
 * tenant-scoped queries take no argument at all (`useGetAdminGroupsQuery()`).
 * Without this, every tenant shares one cache entry and a subscribed query has
 * no reason to refetch after a switch, so it keeps serving the previous
 * tenant's response. That is how another tenant's groups reached the
 * promo-code select (#1848).
 *
 * The tenant must be the SAME value `api/index.ts` stamps onto the `X-Tenant`
 * header, so key and header cannot disagree: a response is filed under exactly
 * the tenant it was requested for.
 *
 * ── Why the tenant is injected rather than imported ──────────────────────
 *
 * This module used to `import { store }` and read the selector directly. That
 * created a genuine cycle — store → every api slice → tenantCacheKey → store —
 * which was "safe" only because the value was read lazily AND the bundler
 * happened to evaluate `store` before the slices. When the entry graph reached
 * this module first, `locationsApiSlice` evaluated while
 * `serializeQueryArgsWithTenant` was still in its temporal dead zone and the
 * whole admin UI died on load with `Cannot access '…' before initialization`.
 *
 * So the dependency is inverted: this module imports nothing of ours and can
 * therefore never be caught half-initialized. `store.ts` registers the tenant
 * source once it has built the store. Until then the source returns '', which
 * only matters if a query somehow ran before the store exists — impossible in
 * practice, and a blank key is a miss, never another tenant's data.
 *
 * See `docs/architecture/tenant-scoped-caching.md`.
 */

type TenantSource = () => string;

const NO_TENANT: TenantSource = () => '';

let readTenant: TenantSource = NO_TENANT;

/**
 * Called once by `store/store.ts` after the store is created. Keeping this a
 * function (rather than a stored store reference) means this module never
 * needs to know what a store is.
 */
export const registerTenantSource = (source: TenantSource): void => {
    readTenant = source;
};

/**
 * The tenant the next request is for — the value `api/index.ts` stamps onto
 * the `X-Tenant` header. Read through the registered source for the same
 * reason the cache key is: `api/index.ts` sits under every api slice
 * (slice → axiosBaseQuery → api/index), so importing the store there closed
 * the cycle store → slices → axiosBaseQuery → api/index → store, and the admin
 * UI died on load with `Cannot access '…' before initialization` whenever the
 * entry graph reached a slice before the store.
 */
export const currentTenant = (): string => readTenant() || '';

/** Test seam: restores the un-registered state. */
export const resetTenantSource = (): void => {
    readTenant = NO_TENANT;
};

export const serializeQueryArgsWithTenant: SerializeQueryArgs<unknown> = (params) => {
    const tenant = readTenant() || '';

    return `${tenant}|${defaultSerializeQueryArgs(params)}`;
};
